import { AlmsyncAdoApiClient } from "@/natsstubs/almsync/ado/client";
import { AlmsyncCommonApiClient } from "@/natsstubs/almsync/common/client";
import { AlmsyncJiraApiClient } from "@/natsstubs/almsync/jira/client";
import { AlmsyncRallyApiClient } from "@/natsstubs/almsync/rally/client";
import { keysToSnake } from "@/natsstubs/wire/transform";

import type { NatsRpcBackend } from "../NatsRpcBackend";
import type { AlmTool, UnknownRecord } from "../RpcBackend";

type SessionClient = Pick<
  AlmsyncJiraApiClient,
  | "start"
  | "forceRunning"
  | "getMapping"
  | "setMapping"
  | "getAlmUser"
  | "setAlmUser"
  | "getAlmUsers"
  | "getAlmTypes"
  | "setAlmType"
  | "loadAlmItemTypes"
>;

interface AlmUser {
  almId?: string | null;
  almName?: string;
  pipId?: string | null;
}

function toWampUser(u: AlmUser): UnknownRecord {
  return { alm_id: u.almId, alm_name: u.almName, pip_id: u.pipId };
}

function reqSid(sid: string | undefined): string {
  if (!sid) {
    throw new Error("almSync session op invoked without a session id");
  }
  return sid;
}

function unwrapAlmTypes(res: unknown): unknown[] {
  const list = (res as { alm_types?: unknown } | null)?.alm_types;
  if (!Array.isArray(list)) {
    throw new Error(
      `almsync get_alm_types: expected { alm_types: [...] }, got ${JSON.stringify(res)}`,
    );
  }
  return list;
}

type Adapter = (args: unknown[], kwargs?: UnknownRecord, sessionId?: string) => Promise<unknown>;

export function registerAlmSyncAdapters(nats: NatsRpcBackend): void {
  const common = new AlmsyncCommonApiClient(nats.caller);
  const jira = new AlmsyncJiraApiClient(nats.caller);
  const ado = new AlmsyncAdoApiClient(nats.caller);
  const rally = new AlmsyncRallyApiClient(nats.caller);
  const clients: Record<AlmTool, SessionClient> = { jira, ado, ac: rally };

  (["jira", "ado", "ac"] as AlmTool[]).forEach((tool) => {
    const client = clients[tool];

    const adapters: Record<string, Adapter> = {
      "session.get_link_types": async (args) =>
        keysToSnake((await common.getLinkTypes(String(args[0]))).linkTypes ?? []),
      "session.delete_link_type": async (args, _kwargs, sid) =>
        common.deleteLinkType(reqSid(sid), String(args[0])),
      "session.update_link_type": async (args, _kwargs, sid) =>
        common.updateLinkType(
          { properties: args[1] as UnknownRecord },
          reqSid(sid),
          String(args[0]),
        ),
      "session.stop": async (args) => common.stop(String(args[0])),
      "session.get_status": async (args, _kwargs, sid) =>
        (await common.getStatus(reqSid(sid ?? (args[0] as string | undefined)))).status,

      "session.start": async (args) => client.start(String(args[0])),
      "session.force_running": async (args) => client.forceRunning(String(args[0])),
      "session.get_mapping": async (args) =>
        keysToSnake((await client.getMapping(String(args[0]))).mapping ?? []),
      "session.set_mapping": async (args) =>
        client.setMapping(
          { mapping: args[1] } as unknown as Parameters<SessionClient["setMapping"]>[0],
          String(args[0]),
        ),
      "session.get_alm_user": async (args) => toWampUser(await client.getAlmUser(String(args[0]))),
      "session.set_alm_user": async (args, _kwargs, sid) =>
        client.setAlmUser({ connectionId: String(args[0]), almId: String(args[1]) }, reqSid(sid)),
      "session.search_alm_users": async (args, _kwargs, sid) => {
        const res = await client.getAlmUsers(
          { connectionId: String(args[0]), query: String(args[1]) },
          reqSid(sid),
        );
        return (res.users ?? []).map(toWampUser);
      },
      "session.get_alm_types": async (args) =>
        unwrapAlmTypes(await client.getAlmTypes(String(args[0]))),
      "session.set_alm_type": async (args, kwargs, sid) =>
        client.setAlmType(
          {
            alm_type: args[1] ?? {},
            art_ids: (args[2] as number[] | undefined) ?? [],
            connection_id: String(kwargs?.connection_id),
          } as unknown as Parameters<SessionClient["setAlmType"]>[0],
          reqSid(sid),
        ),
      "item.load_alm_item_types": async (args, kwargs) => {
        const dryRun = Boolean(kwargs?.dry_run);
        const res = await client.loadAlmItemTypes({ dryRun }, String(args[0]));
        return { success: dryRun || (res.almItemTypes ?? []).length > 0 };
      },
    };

    // WAMP create_link_type maps an existing PI link type to an ALM link type.
    // The NATS ALM-sync create contract takes from/to sticky types instead, so
    // registering it here would corrupt the legacy handler payload.
    nats.register(`almsync.${tool}`, "alm-tools", adapters);

    if (tool === "jira") {
      // Single-ART session reference Kanban board. New endpoints with no WAMP
      // counterpart, so they register under `nats-only` to always route to NATS
      // regardless of the alm-tools rollout flag. args[0] is the pi_session id;
      // consumer reads snake `board_id`, stub returns `boardId`.
      nats.register("almsync.jira", "nats-only", {
        "session.get_session_kanban_board": async (args) => ({
          board_id: (await jira.getJiraSessionKanbanBoard(String(args[0]))).boardId ?? null,
        }),
        "session.set_session_kanban_board": async (args, kwargs) =>
          jira.setJiraSessionKanbanBoard(
            { boardId: (kwargs?.board_id as number | null | undefined) ?? null },
            String(args[0]),
          ),
      });
    }
  });
}
